import axios from "axios";

const PYTHON_API_URL = "https://api.ai.raborisk.ai"
/**
 * Function to call the backend OpenAI API dynamically based on the use case
 * @param {string} useCase - The use case to determine the API behavior (e.g., "suggestControl", "generalChat").
 * @param {object} params - Parameters required for the selected use case. Example: { title, details } for suggestControl or { query } for generalChat.
 * @returns {Promise<string>} - Returns the AI-generated response.
 */
const callOpenAIAPI = async (title, description, threats = []) => {
  try {
    const threatTexts = threats.map(t => t.text ?? t);
    //console.log("threats", threatTexts)
    const { data } = await axios.post(
      `${PYTHON_API_URL}/controls`,
      { title, description, threats: threatTexts },
      { headers: { "Content-Type": "application/json" } }
    );
    return data;
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};


export const callOpenAIAPIChecklist = async (title, description) => {
  try {
    const { data } = await axios.post(
      `${PYTHON_API_URL}/control_checklist`,
      { title, description },                            // same as { ...params }
      { headers: { "Content-Type": "application/json" } }
    );

    // `data` itself is the answer
    return data;                         // no `.reply`
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};

export const callOpenAIAPIPolicyHealthWithFile = async (
  policyDescription,
  relatedCompliance = [],
  file // File | null
) => {
  try {
    const formData = new FormData();
    formData.append("policyDescription", policyDescription);

    if (relatedCompliance?.length) {
      formData.append("relatedCompliance", JSON.stringify(relatedCompliance));
    }

    if (file) {
      formData.append("file", file);
    }

    const { data } = await axios.post(
      `${PYTHON_API_URL}/policy_health_file`,
      formData,
      {
        headers: {
          // IMPORTANT: let axios set boundary automatically
          "Content-Type": "multipart/form-data"
        }
      }
    );

    // data => { "healthScore": 0-100 }
    return data;
  } catch (err) {
    console.error("Error calling Policy Health API:", err);
    throw new Error("Failed to fetch policy health score.");
  }
};

export const callOpenAIAPIPolicyHealthWithFileV2 = async (
  policyDescription,
  relatedCompliance = [],
  file // File | null
) => {
  try {
    const formData = new FormData();
    formData.append("policyDescription", policyDescription);

    if (relatedCompliance?.length) {
      formData.append("relatedCompliance", JSON.stringify(relatedCompliance));
    }

    if (file) {
      formData.append("file", file);
    }

    const { data } = await axios.post(
      `${PYTHON_API_URL}/policy_health_file_v2`,
      formData,
      {
        headers: {
          // Let axios handle boundary
          "Content-Type": "multipart/form-data"
        }
      }
    );

    /*
      data =>
      {
        "healthScore": 0-100,
        "Compliance": 0-100,
        "Completeness": 0-100,
        "Clarity": 0-100,
        "AIRecommendations": [...]
      }
    */

    return data;
  } catch (err) {
    console.error("Error calling Policy Health V2 API:", err);
    throw new Error("Failed to fetch policy health score (gold standard).");
  }
};



export const callOpenAIAPIRisks = async (
  Department_Name,
  Department_Definition,
  Critical_Assets = null   
) => {
  try {
    //console.log("title ", Department_Name, "description ", Department_Definition, "assets ", Critical_Assets);

    const payload = {
      Department_Name,
      Department_Definition,
    };

    // Only include assets if provided
    if (Critical_Assets && Critical_Assets.trim() !== "") {
      payload.Critical_Assets = Critical_Assets;
    }

    const { data } = await axios.post(
      `${PYTHON_API_URL}/department_risks`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );

    return data; // already pure JSON string from backend
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};



export const callOpenAIAPIFindings = async (Checklist, completed) => {
  try {
    ////console.log("text ", Checklist, "completed ", completed)
    const { data } = await axios.post(
      `${PYTHON_API_URL}/checklist_findings`,
      {
        text: Checklist,                        // ⬅️ match Pydantic field
        completed: String(completed).toLowerCase(), // "true"/"false"
      },
      { headers: { "Content-Type": "application/json" } }
    );

    // `data` itself is the answer
    return data;                         // no `.reply`
  } catch (err) {
    console.error("Error calling OpenAI API:", err);
    throw new Error("Failed to fetch AI response.");
  }
};


export const callOpenAIAPISurveyFindings = async (allClauses, answers) => {
  //console.log("Callign AI")
  //console.log("allClauses ", allClauses)
  //console.log("answers ", answers)
  try {
    const { data } = await axios.post(
      `${PYTHON_API_URL}/survey_findings`,
      {
        allClauses,
        answers,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    // data = array of findings
    return data;
  } catch (err) {
    console.error("Error calling Survey Findings AI:", err);
    throw new Error("Failed to generate survey findings.");
  }
};

/**
 * AI DISABLED — DEMO MODE
 * These functions mimic the real API but return safe empty values
 */

const delay = (ms = 150) => new Promise((res) => setTimeout(res, ms));

/**
 * Controls suggestion (returns empty)
 */
// const callOpenAIAPI = async (title, description, threats = []) => {
//   console.warn("AI disabled: callOpenAIAPI");
//   await delay();
//   return ""; // empty string like “no suggestion”
// };

// /**
//  * Control checklist (returns empty array)
//  */
// export const callOpenAIAPIChecklist = async (title, description) => {
//   console.warn("AI disabled: callOpenAIAPIChecklist");
//   await delay();
//   return []; // checklist = []
// };

// /**
//  * Department risks (returns empty array)
//  */
// export const callOpenAIAPIRisks = async (Department_Name, Department_Definition) => {
//   console.warn("AI disabled: callOpenAIAPIRisks");
//   await delay();
//   return []; // no risks
// };

// /**
//  * Checklist findings (returns empty array)
//  */
// export const callOpenAIAPIFindings = async (Checklist, completed) => {
//   console.warn("AI disabled: callOpenAIAPIFindings");
//   await delay();
//   return []; // no findings
// };

export default callOpenAIAPI;


